import { Splide } from '@splidejs/splide';
import { Grid } from '@splidejs/splide-extension-grid';
import '@splidejs/splide/css';
import { windowWidth } from './utils';

const sliders = {
    init() {
     this.initImmediately('.jsUspsSlider', this.textSlider.bind(this));   
     this.initImmediately('.jsHeroSlider', this.heroSlider.bind(this));   
     this.initImmediately('.jsProdSlider', this.prodSlider.bind(this));   
     this.initImmediately('#mainImage', this.setProdThumbnailsSlider.bind(this));
     this.initImmediately('#thumbnails', this.setProdThumbnailsSlider.bind(this));
     this.initOnScroll('.jsBrandsSlider', this.brandsSlider.bind(this));
     this.initOnScroll('.jsIndustrySlider', this.industrySlider.bind(this));
     this.initOnScroll('.jsReviewsSlider', this.reviewsSlider.bind(this));
     this.initOnScroll('.jsTeamSlider', this.teamSlider.bind(this));
     this.initOnScroll('.jsHistorySlider', this.historySlider.bind(this));
     this.initOnScroll('.jsCustomerSlider', this.customerSlider.bind(this));
     this.resetSlider();
    },

        resetSlider() {
        window.addEventListener('reset', () => {
        this.initImmediately('#mainImage', this.setProdThumbnailsSlider.bind(this));
        this.initImmediately('#thumbnails', this.setProdThumbnailsSlider.bind(this)); 
        })
    },

    initImmediately(selector: string, callback: (el: HTMLElement) => void) {
        const elements = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;

        elements.forEach((el) => {
            if (el.dataset.sliderInitialized === 'true') return;

            el.dataset.sliderInitialized = 'true';
            callback(el);
        });
    },

    initOnScroll(selector: string, callback: (el: HTMLElement) => void) {
        const elements = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;

        if (!elements.length) return;

        if (!('IntersectionObserver' in window)) {
            elements.forEach((el) => {
                if (el.dataset.sliderInitialized === 'true') return;

                el.dataset.sliderInitialized = 'true';
                callback(el);
            });

            return;
        }

        const observer = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    const el = entry.target as HTMLElement;

                    if (el.dataset.sliderInitialized === 'true') {
                        obs.unobserve(el);
                        return;
                    }

                    el.dataset.sliderInitialized = 'true';
                    obs.unobserve(el);

                    callback(el);
                });
            },
            {
                root: null,
                rootMargin: '200px 0px',
                threshold: 0,
            },
        );

        elements.forEach((el) => {
            if (el.dataset.sliderInitialized === 'true') return;

            observer.observe(el);
        });
    },

    heroSlider(el: HTMLElement) {
        const loadSlideAssets = (slide: HTMLElement | null) => {
            if (!slide || slide.dataset.assetsLoaded === 'true') return;

            const picture = slide.querySelector('picture');
            if (!picture) return;

            const sources = picture.querySelectorAll('source[data-srcset]');
            const img = picture.querySelector('img[data-src]') as HTMLImageElement | null;

            sources.forEach((source) => {
                const dataSrcset = source.getAttribute('data-srcset');

                if (!dataSrcset) return;

                source.setAttribute('srcset', dataSrcset);
                source.removeAttribute('data-srcset');
            });

            if (img) {
                const dataSrc = img.getAttribute('data-src');

                if (dataSrc) {
                    img.setAttribute('src', dataSrc);
                    img.removeAttribute('data-src');
                }
            }

            slide.dataset.assetsLoaded = 'true';
        };

        const slider = new Splide(el, {
            type: 'slide',
            rewind: true,
            perPage: 1,
            pagination: false,
            arrows: true,
        });

        slider.on('mounted move', () => {
            const activeSlide = el.querySelector('.splide__slide.is-active') as HTMLElement | null;
            const prevSlide = el.querySelector('.splide__slide.is-prev') as HTMLElement | null;
            const nextSlide = el.querySelector('.splide__slide.is-next') as HTMLElement | null;

            loadSlideAssets(activeSlide);
            loadSlideAssets(prevSlide);
            loadSlideAssets(nextSlide);
        });

        slider.mount();
    },

    prodSlider(item: HTMLElement) {
        const slideCount = item.querySelectorAll('.splide__slide').length;

        new Splide(item, {
            type: 'slide',
            arrows: true,
            gap: 10,
            perPage: 5,
            perMove: 1,
            rewind: true,
            speed: 650,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            pagination: slideCount >= 5,
            padding: {right: '7%'},
            breakpoints: {
                1200: {
                    perPage: 2,
                    perMove: 1,
                    padding: {right: '25%'},
                    pagination: slideCount >= 3,
                },

                767: {
                    perPage: 1,
                    perMove: 1,
                    pagination: slideCount >= 2,
                },
            },
        }).mount();
    },

    teamSlider(item: HTMLElement) {
        new Splide(item, {
            type: 'loop',
            arrows: true,
            gap: 15,
            perPage: 4,
            perMove: 1,
            rewind: true,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            pagination: true,

            breakpoints: {
                1200: {
                    perPage: 2,
                    perMove: 1,
                    pagination: true,
                },

                767: {
                    perPage: 1,
                    perMove: 1,
                    pagination: true,
                },
            },
        }).mount();
    },
    historySlider(item: HTMLElement) {
        new Splide(item, {
            type: 'loop',
            arrows: true,
            perPage: 1,
            perMove: 1,
            rewind: true,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            pagination: false,
        }).mount();
    },

    reviewsSlider(item: HTMLElement) {
    
        new Splide(item, {
            type: 'slide',
            arrows: true,
            gap: 30,
            perPage: 2,
            perMove: 1,
            pagination: true,
            rewind: true,
            speed: 650,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            breakpoints: {
                1200: {
                    perPage: 1,
                    perMove: 1,
                    grid: {
                        rows: 2, 
                        cols: 1,
                        gap: {
                            row: '20px',
                            col: '20px'
                        }
                    }
                },

                767: {
                    perPage: 1,
                    perMove: 1,
                    grid: {
                        rows: 2, 
                        cols: 1,
                        gap: {
                            row: '20px',
                            col: '20px'
                        }
                    }
                },
            },
        }).mount({Grid});
    },
    customerSlider(item: HTMLElement) {
        new Splide(item, {
            type: 'loop',
            arrows: false,
            gap: 20,
            perPage: 6,
            perMove: 1,
            pagination: true,
            autoplay: true,
            rewind: true,
            speed: 650,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            reducedMotion: {
            interval: 4000,
            speed: 800,
            autoplay: true,
            },
            breakpoints: {
                1200: {
                    gap: 15,
                    perPage: 3,
                    perMove: 1,
                },

                767: {
                    perPage: 1,
                    perMove: 1,
                },


            },
        }).mount();
    },

    brandsSlider(item: HTMLElement) {
        
        new Splide(item, {
            type: 'loop',
            arrows: true,
            pagination: false,
            gap: 14,
            perPage: 8,
            perMove: 1,
            speed: 650,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            breakpoints: {
                1200: {
                perPage: 1, 
                perMove: 1,
            grid: {
                    rows: 2,
                    cols: 4,
                    gap : {
                        row: '15px',
                        col: '25px',
                    },
                }
                },

                767: {
                perPage: 1, 
                perMove: 1,
            grid: {
                    rows: 2,
                    cols: 2,
                    gap : {
                        row: '15px',
                        col: '15px',
                    },
                }
                },
            },
        }).mount({ Grid });
    },

    industrySlider(item: HTMLElement) {

        new Splide(item, {
            type: 'loop',
            arrows: true,
            pagination: true,
            gap: 13,
            perPage: 5,
            perMove: 1,
            speed: 650,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            waitForTransition: true,
            breakpoints: {
                1200: {
                gap: 24,
                perPage: 3, 
                perMove: 1,
                },

                767: {
                gap: 15,
                perPage: 2, 
                perMove: 1,  
                },
            },
        }).mount();
    },


    textSlider(item: HTMLElement) {
        new Splide(item, {
            type: 'loop',
            perPage: 1, 
            perMove: 1, 
            arrows: false,
            pagination: false,
            destroy: true,
            reducedMotion: {
            interval: 4000,
            speed: 800,
            autoplay: true,
            },
            breakpoints: {
            1200: {
            destroy: false,
            }
            },
        }).mount();
    }, 

    setProdThumbnailsSlider(item: HTMLElement) {
    const productMainImages = document.getElementById("mainImage") as HTMLElement | undefined
    const thumbnails = document.getElementById("thumbnails") as HTMLElement | undefined

    if (productMainImages && thumbnails) {

    const slideCount = productMainImages.querySelectorAll('.splide__slide').length;

      const main = new Splide(productMainImages, {
        type: "fade",
        rewind: true,
        pagination: false,
        arrows: false,         
         breakpoints: {
            1200: {
            pagination: slideCount > 1,
            }
         }
    })

      const thumbnailsNav = new Splide(thumbnails, {
        type: 'slide',
        direction: "ttb",
        isNavigation: true,
        pagination: false,
        rewind: true,
        height: '390px',
        arrows: slideCount > 3, 
        gap: "10px",
      })

      main.sync(thumbnailsNav)
      main.mount()
      thumbnailsNav.mount()
    }
  },
  

};

export default sliders;